import { type SelectOwnerState } from '@mui/base'
import { clsx } from 'clsx'
import { type RangeBoundaries } from 'instantsearch.js/es/connectors/range/connectRange'
import { type ComponentProps, type JSX, useEffect, useState } from 'react'

import { Input, Slider } from 'base-ui'

import { useProductSearchRefinementContext } from '../ProductSearchRefinement'
import { ProductSearchRefinementClearRefinement } from '../ProductSearchRefinementClearRefinement'

const DELIMITER_LABEL = '—'
const PERCENTAGE_SYMBOL = '%'

type Range = {
  max?: number
  min?: number
}

const DISCOUNT_DEFAULT_VALUES = {
  max: 100,
  min: 0,
} satisfies Range

const getInitialValues = (params: {
  range: Range
  start: RangeBoundaries
}): { from: number; to: number } => {
  const { range, start } = params
  const {
    max = DISCOUNT_DEFAULT_VALUES.max,
    min = DISCOUNT_DEFAULT_VALUES.min,
  } = range
  const from = Math.max(
    min,
    start[0] && Number.isFinite(start[0]) ? start[0] : min,
  )
  const to = Math.min(
    max,
    start[1] && Number.isFinite(start[1]) ? start[1] : max,
  )

  return {
    from,
    to,
  }
}

const getDefaultRange = (range: Range): Required<Range> => {
  const { max, min } = range

  return {
    max: max ?? DISCOUNT_DEFAULT_VALUES.max,
    min: min ?? DISCOUNT_DEFAULT_VALUES.min,
  }
}

type ProductSearchRefinementDiscountListboxBodyProps<
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
> = ComponentProps<'div'> & {
  ownerState: SelectOwnerState<OptionValue, Multiple>
}

export const ProductSearchRefinementDiscountListboxBody = <
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
>(
  props: ProductSearchRefinementDiscountListboxBodyProps<OptionValue, Multiple>,
): JSX.Element => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- not used for rendering
    children,
    className,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- not used for rendering
    ownerState,
    ...restProps
  } = props

  const refinementContext = useProductSearchRefinementContext()

  if (!('refinementRange' in refinementContext)) {
    throw new Error('The refinement is not a range refinement.')
  }

  const { refinementRange, refinementRangeOptions } = refinementContext

  const { attribute } = refinementRangeOptions

  const { canRefine, range, refine, start } = refinementRange

  const { max, min } = getDefaultRange(range)

  const [inputRange, setInputRange] = useState(
    getInitialValues({ range, start }),
  )
  const { from, to } = inputRange

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
    setInputRange(getInitialValues({ range, start }))
  }, [range, start])

  return (
    <div
      className={clsx(
        className,
        'flex flex-col gap-2 bg-light-primary-medium p-4 desktop:gap-6 desktop:bg-transparent desktop:ps-6 desktop:pt-3',
      )}
      {...restProps}
    >
      <Slider
        className="w-full"
        defaultValue={[min, max]}
        disabled={!canRefine}
        max={max}
        min={min}
        onChange={(event, values) => {
          if (Array.isArray(values)) {
            setInputRange({ from: values[0], to: values[1] })
          }
        }}
        onChangeCommitted={() => {
          refine([from, to])
        }}
        value={[from, to]}
      />

      <div className="flex items-center gap-4 self-stretch desktop:gap-1">
        <Input
          className="flex-1"
          disabled={!canRefine}
          endAdornment={PERCENTAGE_SYMBOL}
          onChange={(event) => {
            const value = Number(event.target.value)
            setInputRange({ from: value, to })
          }}
          onKeyUp={() => {
            refine([from, to])
          }}
          slotProps={{
            input: {
              className: 'w-full text-right',
              'data-qa-id': 'form-productFilter__discountRangeInput__min',
            },
          }}
          value={from}
        />
        {DELIMITER_LABEL}
        <Input
          className="flex-1"
          disabled={!canRefine}
          endAdornment={PERCENTAGE_SYMBOL}
          onChange={(event) => {
            const value = Number(event.target.value)
            setInputRange({ from, to: value })
          }}
          onKeyUp={() => {
            refine([from, to])
          }}
          slotProps={{
            input: {
              className: 'w-full text-right',
              'data-qa-id': 'form-productFilter__discountRangeInput__max',
            },
          }}
          value={to}
        />
      </div>

      <ProductSearchRefinementClearRefinement
        className="hidden self-start desktop:inline-flex"
        includedAttributes={[attribute]}
        onResetFilter={(): void => {
          setInputRange({ from: min, to: max })
        }}
      />
    </div>
  )
}
